import { Component } from 'react';
import { Sentry } from '../../analytics/sentry';
import { isChunkLoadError } from '../../utils/lazyWithRetry';

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('ErrorBoundary caught:', error, info.componentStack);
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack } },
    });
  }

  handleHardRefresh = async () => {
    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((r) => r.unregister()));
      }
    } catch { /* best-effort */ }
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch { /* best-effort */ }
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Chunk load errors mean the user's app is stale after a deployment.
      // Show a specific message directing them to refresh instead of the
      // generic "something went wrong" copy.
      if (isChunkLoadError(this.state.error)) {
        return (
          <div className="min-h-[50vh] flex items-center justify-center p-8">
            <div className="text-center max-w-md">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">New version available</h2>
              <p className="text-sm text-gray-600 mb-4">
                A new version of Activ OS has been deployed. Please refresh to load the latest update.
              </p>
              <button
                onClick={this.handleHardRefresh}
                className="px-4 py-2 bg-activ-teal text-white rounded-lg text-sm hover:bg-activ-navy transition-colors"
              >
                Refresh now
              </button>
            </div>
          </div>
        );
      }

      return (
        <div className="min-h-[50vh] flex items-center justify-center p-8">
          <div className="text-center max-w-md">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-sm text-gray-600 mb-4">
              {this.state.error?.message || 'An unexpected error occurred.'}
            </p>
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              className="px-4 py-2 bg-activ-teal text-white rounded-lg text-sm hover:bg-activ-navy transition-colors"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
